import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import * as React from "react";
import { MessageInput } from "@/views/open-chat/components/messageInput";
import { useQaBusinessReportsOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import { ulid } from "ulid";
import { toast } from "react-toastify";
import useWebSocket, { ReadyState } from "react-use-websocket";
import {
  SnapshotSearchEvent,
  SnapshotSearchQa,
} from "@/modules/snapshots/snapshotCollections/components/outcomes/search/types";
import { OpenChatMessageRow } from "@/views/open-chat/components/message/messageRow";
import { Loader } from "@/components/ui/loader";
import { OpenChatMessage } from "@/types/open-chat";
import { PromptGeneratorContextProvider } from "@/components/feature/promptGenerator/contextProvider";
import * as analytics from "@/services/analytics";
import { formatSnapshotQaWithLink } from "@/modules/snapshots/snapshotCollections/components/outcomes/qa/helpers";

interface Props {
  selectedCollection: SnapshotCollection;
}

export const SnapshotCollectionsOutcomesQA: React.FC<Props> = ({ selectedCollection }) => {
  const [searchId] = React.useState<string>(() => ulid());
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [messages, setMessages] = React.useState<OpenChatMessage[]>([]);

  const [searchQuery] = useQaBusinessReportsOutcomeQuery({
    refetchOnFocus: false,
  });
  const contentRef = React.useRef<HTMLDivElement>(null);

  const onSendMessage = async (message: string) => {
    try {
      setIsLoading(true);
      await searchQuery({
        business_report_id: selectedCollection.business_report_id,
        snapshot_collection_id: selectedCollection.id,
        query: message,
        search_id: searchId,
      }).unwrap();
      analytics.snapshotQa(message);

      setMessages(prevMessages => [...prevMessages, { id: ulid(), content: message, type: "human" }]);
    } catch {
      setIsLoading(false);
      toast.error("Error searching for snapshots");
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (contentRef.current !== null && messages.length) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [messages.length]);

  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket<SnapshotSearchEvent>(
    "/pf-business-reports/ws",
    {
      shouldReconnect: event => event?.code !== 1000,
      skipAssert: true,
      reconnectInterval: 3000,
    },
  );

  React.useEffect(() => {
    if (!lastJsonMessage) return;
    if (lastJsonMessage.event === "qa_response") {
      const response = JSON.parse(lastJsonMessage.context) as SnapshotSearchQa;

      setMessages(prevMessages => [
        ...prevMessages,
        {
          id: ulid(),
          content: formatSnapshotQaWithLink(response.answer, response.citations),
          type: "ai",
        },
      ]);

      setIsLoading(false);
    }
  }, [lastJsonMessage, setMessages]);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && searchId) {
      sendJsonMessage({ query_id: searchId });
    }
  }, [readyState, searchId, sendJsonMessage]);

  return (
    <PromptGeneratorContextProvider>
      <div className="h mr-4 flex h-[400px] flex-grow flex-col overflow-hidden rounded-xl shadow">
        <div className="flex flex-grow flex-col overflow-y-scroll bg-user-message p-4" ref={contentRef}>
          {messages.map(message => (
            <OpenChatMessageRow
              isImprovement={false}
              allowCopy={true}
              allowImprovement={false}
              key={message.id}
              hideIcons={true}
              message={message}
              className="w-full"
            />
          ))}
          {isLoading && <Loader />}
        </div>
        <div className="flex flex-col bg-white p-2">
          <div className="mt-2">
            <MessageInput
              isReady={readyState === ReadyState.OPEN && !!searchId}
              onSendMessage={onSendMessage}
              isStreaming={false}
            />
          </div>
        </div>
      </div>
    </PromptGeneratorContextProvider>
  );
};
